import React from "react";
import { useSearchParams } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { EditableTagList } from "../Shared/NameTagEdit/EditableTagList";
import { useMetaEditor } from "../Shared/NameTagEdit/useMetaEditor";
import { DetailRow } from "../Shared/DetailRow";
import { DetailLink } from "../Shared/DetailLink";
import CarrierAccessSummary from "./CarrierAccessSummary";
import ExtenderPopConnectionsSummary from "./ExtenderPopConnectionsSummary";
import { cpeDetailPath, extenderPopStatusPresentation } from "./ExtenderPopData";
import {
  LoadingDetail,
  RoleTag,
  formatLocation,
  formatRenewDate,
  formatTerm,
  legacyTagsNoop,
  useExtenderPopDetail,
} from "./ExtenderPopDetailShared";
import type { ExtenderPopCpeDetail } from "./extenderPopDetail.types";

/**
 * The site's routers, one row each: its id (which opens the CPE's detail),
 * name, role, status and term. This is where an HA pair reads as a pair.
 */
const CpesCard: React.FC<{
  extenderPopId: number;
  cpes: ExtenderPopCpeDetail[];
}> = ({ extenderPopId, cpes }) => (
  <GenericCard
    isCollapsible={false}
    headerContent="CPEs"
    bodyContent={
      cpes.length === 0 ? (
        <p className="py-1.5 text-sm text-semantic-text-secondary">
          No CPE assigned to this Extended PoP yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-semantic-text-secondary">
                <th className="py-1.5 pr-4 font-normal">CPE ID</th>
                <th className="py-1.5 pr-4 font-normal">Name</th>
                <th className="py-1.5 pr-4 font-normal">Role</th>
                <th className="py-1.5 pr-4 font-normal">Status</th>
                <th className="py-1.5 pr-4 font-normal">Term</th>
                <th className="py-1.5 font-normal">Renew Date</th>
              </tr>
            </thead>
            <tbody>
              {cpes.map((cpe) => {
                const status = extenderPopStatusPresentation(cpe.status);
                return (
                  <tr
                    key={cpe.id}
                    className="border-t border-semantic-border-secondary text-semantic-text-primary"
                  >
                    <td className="py-1.5 pr-4">
                      <DetailLink
                        to={cpeDetailPath(extenderPopId, cpe.id)}
                        label={cpe.serviceId}
                      />
                    </td>
                    <td className="py-1.5 pr-4">{cpe.name ?? "-"}</td>
                    <td className="py-1.5 pr-4">
                      <RoleTag role={cpe.role} />
                      {!cpe.role && "-"}
                    </td>
                    <td className="py-1.5 pr-4">
                      <GenericTag
                        variant={status.variant}
                        label={status.label}
                        size="small"
                      />
                    </td>
                    <td className="py-1.5 pr-4">
                      {formatTerm(cpe.subscription) ?? "-"}
                    </td>
                    <td className="py-1.5">
                      {formatRenewDate(cpe.subscription) ?? "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )
    }
  />
);

/**
 * Detail view for one Extended PoP: the customer site (85-), homologated
 * with Port Details — details left, Status right — then its CPEs and a
 * summary of what the whole site delivers. Each CPE has its own detail with
 * its links, bandwidth and connections (ExtenderPopCpeDetails).
 * Customer-facing on purpose — no internal hostnames or hardware identity.
 */
const ExtenderPopDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const extenderPopId = Number(searchParams.get("extenderPopId"));
  const { tenantId } = useAppSelector((state) => state.selection);
  const api = useAxiosAuth0();

  const { data: detail, error } = useExtenderPopDetail(extenderPopId, tenantId);

  // The name is edited in place, like Port Details. Tags are not: they go
  // through EditableTagList and the Tags API.
  const meta = useMetaEditor({
    api,
    kind: "extender-pop",
    resourceId: detail ? String(detail.id) : null,
    tenantId,
    initialName: detail?.name ?? "",
    initialTag: "",
  });

  const saveName = (newValue: string) => {
    const nextName = newValue.trim();
    if (!nextName) {
      meta.cancelName();
      return;
    }
    meta.setNameValue(nextName);
    meta.saveName(nextName).catch(() => meta.cancelName());
  };

  if (error) {
    return <ErrorFetchingItems text={error} />;
  }

  if (!detail) {
    return <LoadingDetail />;
  }

  const status = extenderPopStatusPresentation(detail.status);
  const activeCpe = detail.cpes.find((cpe) => cpe.role === "active");

  return (
    <div className="px-4 overflow-y-auto h-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        {/* Card 1 — homolog of "Port Details" */}
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Extended PoP Details"
            bodyContent={
              <div className="space-y-2">
                <DetailRow label="ID" value={detail.serviceId} />
                <DetailRow
                  label="Name"
                  value={
                    <GenericTag
                      label={meta.nameValue || "—"}
                      variant="neutral"
                      size="small"
                      minWidth="100px"
                      className="text-center"
                      editable={true}
                      value={meta.nameValue}
                      onSave={saveName}
                      onCancel={meta.cancelName}
                      isSaving={meta.savingField === "name"}
                      disabled={meta.savingField !== null}
                    />
                  }
                />
                <div className="flex justify-between w-full py-1 border-b">
                  <h3 className="text-sm text-semantic-text-primary">Tag</h3>
                  <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                    <EditableTagList
                      tags={[]}
                      onUpdate={legacyTagsNoop}
                      resourceType="ke-extender-pop"
                      resourceId={detail.id}
                      tenantId={detail.tenantId}
                    />
                  </div>
                </div>
                <DetailRow
                  label="Location"
                  value={formatLocation(detail.site)}
                />
                {/* One entry per sold access, by id, as the list shows it. */}
                <DetailRow
                  label="Carrier Access"
                  value={
                    detail.carrierAccess.length > 0 ? (
                      <CarrierAccessSummary
                        carriers={detail.carrierAccess}
                        align="end"
                      />
                    ) : null
                  }
                  isLast
                />
              </div>
            }
          />
        </div>

        {/* Card 2 — homolog of "Status" */}
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Status"
            bodyContent={
              <div className="space-y-2">
                <DetailRow
                  label="Service Status"
                  value={
                    <GenericTag
                      variant={status.variant}
                      label={status.label}
                      size="small"
                    />
                  }
                />
                <DetailRow
                  label="Active CPE"
                  value={
                    activeCpe ? (
                      <DetailLink
                        to={cpeDetailPath(detail.id, activeCpe.id)}
                        label={activeCpe.serviceId}
                      />
                    ) : null
                  }
                  isLast
                />
              </div>
            }
          />
        </div>

        {/* Card 3 — the site's routers, the pair side by side */}
        <div className="flex w-full self-start md:col-span-2">
          <CpesCard extenderPopId={detail.id} cpes={detail.cpes} />
        </div>

        {/* Card 4 — what the whole site delivers, by type */}
        <div className="flex w-full self-start md:col-span-2 pb-4">
          <ExtenderPopConnectionsSummary
            extenderPopId={detail.id}
            tenantId={tenantId}
          />
        </div>
      </div>
    </div>
  );
};

export default ExtenderPopDetails;
